/**
 * aiWakeGuard.js · iter r97e · User M1520
 *
 * Insieme di helper per proteggere le operazioni AI dal backgrounding iOS:
 *
 * 1. `withWakeLock(fn)` — wrapper che tiene lo schermo attivo (via
 *    navigator.wakeLock.request('screen')) durante l'esecuzione di `fn`.
 *    Su iOS 16.4+ questo previene la sospensione della WKWebView per la
 *    maggior parte dei casi. Se il wakeLock non è supportato, `fn` gira
 *    comunque senza errori.
 *
 * 2. `newRequestId()` — genera un UUID v4 usato come `client_request_id`
 *    lato server per idempotency (recovery al foreground).
 *
 * 3. `recoverArtifact(api, requestId)` — chiama `GET /api/live/recover/{id}`
 *    per recuperare un artifact già completato dal backend. Torna:
 *      { found: true, id, before_path, after_path, kind, style } oppure
 *      { found: false }.
 *
 * 4. `PendingAiOps` — piccolo store in localStorage per tracciare le
 *    operazioni AI in corso, così al `visibilitychange visible` possiamo
 *    ritentare il recovery automatico.
 */

const LS_KEY = "pronto_ai_pending_ops_v1";

/** UUID v4 leggero — usa crypto.randomUUID se disponibile, altrimenti fallback. */
export function newRequestId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch { /* */ }
  // Fallback RFC4122-ish
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Richiede un wake lock schermo. Ritorna un handle con `release()`. */
async function acquireWakeLock() {
  try {
    if (typeof navigator !== "undefined" && navigator.wakeLock?.request) {
      const wl = await navigator.wakeLock.request("screen");
      let released = false;
      const release = async () => {
        if (released) return;
        released = true;
        try { await wl.release(); } catch { /* */ }
      };
      // Se il tab perde visibilità e poi torna, il wakeLock viene rilasciato
      // automaticamente da iOS: proviamo a ri-acquisirlo al ritorno.
      return { release, native: wl };
    }
  } catch { /* wakeLock non supportato o negato */ }
  return { release: async () => {}, native: null };
}

/** Esegue `fn` mantenendo lo schermo attivo (wakeLock). Rilascia sempre in finally. */
export async function withWakeLock(fn) {
  const handle = await acquireWakeLock();
  try {
    return await fn();
  } finally {
    await handle.release();
  }
}

/** Chiama l'endpoint di recovery per recuperare un artifact già completato. */
export async function recoverArtifact(api, requestId) {
  if (!requestId) return { found: false };
  try {
    const { data } = await api.get(`/live/recover/${encodeURIComponent(requestId)}`, {
      timeout: 8000,
    });
    return data || { found: false };
  } catch {
    return { found: false };
  }
}

// ─────────────────────────────────────────────────────────────────
// PendingAiOps · store localStorage per resume al visibilitychange
// ─────────────────────────────────────────────────────────────────

function readOps() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeOps(ops) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(ops || []));
  } catch { /* quota / mode privato */ }
}

/** Registra un'operazione AI in corso. Ritorna la chiave per rimuoverla dopo. */
export function trackPendingAiOp({ requestId, mode, itemId, propertyId }) {
  if (!requestId) return;
  const ops = readOps();
  // Rimuovo eventuali entry duplicate con lo stesso requestId
  const filtered = ops.filter((o) => o.requestId !== requestId);
  // Espiro le entry più vecchie di 10 minuti (recovery ha senso solo poco dopo)
  const now = Date.now();
  const pruned = filtered.filter((o) => (now - (o.ts || 0)) < 10 * 60 * 1000);
  pruned.push({ requestId, mode, itemId, propertyId, ts: now });
  writeOps(pruned);
}

/** Rimuove un'operazione tracked (chiamare a success o quando l'utente ha visto il risultato). */
export function clearPendingAiOp(requestId) {
  if (!requestId) return;
  const ops = readOps();
  writeOps(ops.filter((o) => o.requestId !== requestId));
}

/** Ritorna la lista di operazioni pending non ancora recuperate. */
export function listPendingAiOps() {
  // Auto-pruning al read
  const ops = readOps();
  const now = Date.now();
  const alive = ops.filter((o) => (now - (o.ts || 0)) < 10 * 60 * 1000);
  if (alive.length !== ops.length) writeOps(alive);
  return alive;
}
